import { TooltipModel, TooltipModelProps } from '@mail/3promo/models/tooltip';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import { SafetyLocalStorage } from '@mail-core/std';
import { xray } from 'lib/xray';
import { IS_PHONE_BROWSER, USER_EMAIL } from 'reactApp/appHelpers/configHelpers';
import { familySettingsStackTooltip, familySettingsStackTooltipConfig } from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import { FamilySettingsStackTooltip } from 'reactApp/ui/FamilySettingsStack/FamilySettingsStackTooltip/FamilySettingsStackTooltip';

const TOOLTIP_CLOSED_KEY = 'family_settings_stack_tooltip_closed';
const TOOLTIP_SHOW_COUNT_KEY = 'family_settings_stack_tooltip_show_count';
const TOOLTIP_LAST_SHOWN_KEY = 'family_settings_stack_tooltip_last_shown';

const TOOLTIP_KEY = 'family-settings-stack-tooltip';

export const FamilySettingsStackTooltipCreator: ModelCreator = ({ models, render, stores }) => {
    const [maxShows, intervalMs] = familySettingsStackTooltipConfig;

    const email = USER_EMAIL;
    const lStorage = new SafetyLocalStorage({});

    const selector = '[data-qa-id="family-settings-stack"]';

    return new TooltipModel(
        TOOLTIP_KEY,
        models.base,
        new TooltipModelProps(TOOLTIP_KEY, {
            store: new LocalStorageStore(TOOLTIP_KEY, stores.localStorage),
            renderer: new ReactRenderer(TOOLTIP_KEY, FamilySettingsStackTooltip as any, render.react),
            closable: true,
            hideOnOutsideClick: true,
            isModelEnabledCheck: () => true,
            ignoreTestOnRedraw: true,
            test: (resolve, reject) => {
                if (!familySettingsStackTooltip) {
                    reject();
                    return;
                }
                if (IS_PHONE_BROWSER) {
                    reject();
                    return;
                }
                const target = document.querySelector(selector) as HTMLElement;
                if (!target) {
                    reject();
                    return;
                }
                const closed = lStorage.getItem(`${email}|${TOOLTIP_CLOSED_KEY}`);
                if (closed) {
                    reject();
                    return;
                }
                const shown = parseInt(lStorage.getItem(`${email}|${TOOLTIP_SHOW_COUNT_KEY}`) || '0');
                if (shown >= maxShows) {
                    reject();
                    return;
                }
                const last = lStorage.getItem(`${email}|${TOOLTIP_LAST_SHOWN_KEY}`);
                if (last) {
                    const lastTs = parseInt(last) || 0;
                    if (Date.now() - lastTs < intervalMs) {
                        reject();
                        return;
                    }
                }
                resolve();
            },
            templateData: {
                title: 'Тут можно добавлять и удалять участников подписки',
                text: 'И видеть, сколько ГБ каждый занимает в Облаке семьи',
                targetSelector: selector,
                placement: 'bottom-end',
                leftOffset: 0,
                onCrossClose: () => {
                    lStorage.setItem(`${email}|${TOOLTIP_CLOSED_KEY}`, 'true');
                    xray.send('family-settings-stack-tooltip-cross');
                },
                onClick: () => {
                    lStorage.setItem(`${email}|${TOOLTIP_CLOSED_KEY}`, 'true');
                    xray.send('family-settings-stack-tooltip-click');
                },
            },
            getTarget: () => {
                return document.querySelector(selector) as HTMLElement;
            },
            onShow: () => {
                const showCount = parseInt(lStorage.getItem(`${email}|${TOOLTIP_SHOW_COUNT_KEY}`) || '0');
                const lastShownStr = lStorage.getItem(`${email}|${TOOLTIP_LAST_SHOWN_KEY}`) || '0';
                const lastShown = parseInt(lastShownStr) || 0;
                const now = Date.now();
                if (now - lastShown >= 1000) {
                    lStorage.setItem(`${email}|${TOOLTIP_SHOW_COUNT_KEY}`, String(showCount + 1));
                    lStorage.setItem(`${email}|${TOOLTIP_LAST_SHOWN_KEY}`, String(now));
                }
                xray.send('family-settings-stack-tooltip-show');
            },
            onClose: () => {
                xray.send('family-settings-stack-tooltip-close');
            },
        })
    );
};
